// Derive facets (price/volume ranges, color palette, intended-use list,
// frame-system list) from a flat list of items. NetSuite can either:
//   a) compute these server-side and return them as a `facets` block, or
//   b) return raw items and let the SPA derive — useful for prototyping.
// We try (a) first and fall back to (b).

export function deriveFacets(items = [], displayCurrency = "USD") {
  const prices = items.map((i) => priceAmount(i, displayCurrency)).filter((n) => Number.isFinite(n));
  const volumes = items.map((i) => Number(i.volume)).filter((n) => Number.isFinite(n));

  const colorMap = new Map();
  const useSet = new Set();
  const frameSet = new Set();
  for (const it of items) {
    for (const c of it.colors || []) {
      const key = c.id ?? c.name;
      if (!colorMap.has(key)) colorMap.set(key, { id: key, name: c.name, hex: c.hex, image: c.image });
    }
    for (const u of it.intendedUse || []) useSet.add(u);
    if (it.frameSystem) frameSet.add(it.frameSystem);
  }

  return {
    price: { min: Math.floor(Math.min(...prices, 0) || 0), max: Math.ceil(Math.max(...prices, 0)) || 0 },
    volume: { min: Math.floor(Math.min(...volumes, 0) || 0), max: Math.ceil(Math.max(...volumes, 0)) || 0 },
    colors: Array.from(colorMap.values()),
    intendedUse: Array.from(useSet),
    frameSystem: Array.from(frameSet),
  };
}

function priceAmount(item, currency) {
  if (Array.isArray(item.prices) && item.prices.length) {
    const m = item.prices.find((p) => p.currency === currency) || item.prices[0];
    return Number(m.amount);
  }
  if (item.price && typeof item.price === "object") return Number(item.price.amount);
  return Number(item.price);
}

export function applyFilters(items, value, displayCurrency = "USD") {
  if (!value) return items;
  return items.filter((it) => {
    if (value.priceRange) {
      const p = priceAmount(it, displayCurrency);
      if (Number.isFinite(p) && (p < value.priceRange[0] || p > value.priceRange[1])) return false;
    }
    if (value.volumeRange) {
      const v = Number(it.volume);
      if (Number.isFinite(v) && (v < value.volumeRange[0] || v > value.volumeRange[1])) return false;
    }
    if (value.colors?.length) {
      const ids = (it.colors || []).map((c) => c.id ?? c.name);
      if (!ids.some((id) => value.colors.includes(id))) return false;
    }
    if (value.intendedUse?.length) {
      const u = it.intendedUse || [];
      if (!u.some((x) => value.intendedUse.includes(x))) return false;
    }
    if (value.frameSystem?.length) {
      if (!value.frameSystem.includes(it.frameSystem)) return false;
    }
    return true;
  });
}

export function applySort(items, sort, displayCurrency = "USD") {
  const out = [...items];
  switch (sort) {
    case "price_asc":  out.sort((a, b) => priceAmount(a, displayCurrency) - priceAmount(b, displayCurrency)); break;
    case "price_desc": out.sort((a, b) => priceAmount(b, displayCurrency) - priceAmount(a, displayCurrency)); break;
    case "name_asc":   out.sort((a, b) => (a.name || "").localeCompare(b.name || "")); break;
    case "newest":     out.sort((a, b) => Number(!!b.isNew) - Number(!!a.isNew)); break;
    default: /* relevance: leave server order */ break;
  }
  return out;
}
